import { createContext, PropsWithChildren, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import useLogin from 'shared/Providers/User/use-login';
import { TUser, TUserPermission } from 'shared/model/user-types';
import { emptyUser } from 'shared/model/user-utils';
import { TToken } from 'shared/model/token-types';
import LoginLayout from 'shared/Pages/common/Login/login-layout';
import Home from 'shared/Pages/Site/Home';
import { isWeb } from 'shared/constants/modules';
import { r as allRoutes } from 'shared/constants/routes';
import WaitingScreen from '../WaitingScreen';
import SharedApp from 'shared/Pages/Shared/App/SharedApp';
import { useSharingContext } from 'shared/Providers/Sharing';

type TUserContext = {
	token?: TToken;
	setToken: (t?: TToken) => void;
	user?: TUser;
	isLoggingIn: boolean;
	isRestoring: boolean;
	id: string; // TODO: move to `user`?
	databaseId: string; // TODO: move to `user`?
	permissions: Record<TUserPermission, boolean>;
	canSeeExperiments: boolean;
	login: (params: { email: string, password: string, rememberMe?: boolean }) => Promise<boolean>;
	logout: () => Promise<void>;
};

export const UserContext = createContext<TUserContext>({
	token: undefined,
	setToken: () => undefined,
	user: { ...emptyUser },
	isLoggingIn: false,
	isRestoring: true,
	id: '',
	databaseId: '',
	permissions: { ...emptyUser.permissions },
	canSeeExperiments: false,
	login: async () => false,
	logout: async () => undefined,
});

export const UserProvider = ({ children }: PropsWithChildren) => {
	const { isLoggingIn, isRestoring, login, logout, user, token, setToken } = useLogin();
	const { sharingId } = useSharingContext();
	const { pathname } = useLocation();

	console.log('tst: UserProvider', isLoggingIn, !!token, !!user);

	if (isRestoring) {
		return <WaitingScreen description="Restoring session ..." />;
	}

	let content: JSX.Element;
	if (sharingId) {
		content = <SharedApp />;
	} else if (user?.databaseId) {
		content = <>{children}</>;
	} else if (isWeb && pathname === allRoutes.home.path) {
		// Public homepage (web only). Its "Log in" button goes to /login, which renders the login layout.
		content = <Home />;
	} else {
		content = <LoginLayout />;
	}

	return (
		<UserContext.Provider
			value={{
				token,
				setToken,
				user,
				isLoggingIn,
				isRestoring,
				id: user?.id ?? '', // TODO: remove, use `user.id` instead
				databaseId: user?.databaseId ?? '', // TODO: remove, use `user.databaseId` instead
				permissions: user?.permissions ?? ({} as Record<TUserPermission, boolean>), // TODO: remove, use `user.permissions` instead
				canSeeExperiments: !!user?.roles?.find((r) => r.key === 'SuperAdmin'),
				login,
				logout,
			}}
		>
			{content}
		</UserContext.Provider>
	);
};

export const useUserContext = (): TUserContext => useContext(UserContext);
